import { useRepository } from '@city-sage/aurum-avis-labs-components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { MapPin, Calendar, Heart, Compass } from 'lucide-react';
import PublicStopsMap, { ZoomMode } from '../main-map/PublicStopsMap';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useDebouncedCallback } from '../../lib/hooks/useDebounce';
import { ItineraryProjection, StopProjection } from '@city-sage/core-client';
import { LocationCard } from '../main-map/LocationCard';
import { Image } from '@city-sage/aurum-avis-labs-components';
import type { LngLatLike, LngLatBoundsLike } from 'mapbox-gl';

import { AggregatedStopPreviewSheet } from '../main-map/AggregatedStopPreviewSheet';
import { AggregatedStopProjection } from '../main-map/types';
import { WelcomeModal } from '../WelcomeModal';
import { HOME_PAGE_SEO } from '@city-sage/aurum-avis-labs-components';

interface MapViewState {
  center: LngLatLike;
  zoom: number;
}

interface LocationData {
  location: string;
  itineraryCount: number;
  itineraries: ItineraryProjection[];
  countryImageUrl: string | null;
}

// Interface for mini itinerary card - updated to use ItineraryProjection
interface MiniItineraryCardProps {
  itinerary: ItineraryProjection;
  onClick?: () => void;
}

// Mini itinerary card component for the lower zoom level
const MiniItineraryCard: React.FC<MiniItineraryCardProps> = ({
  itinerary,
  onClick,
}) => {
  return (
    <motion.div className="flex-shrink-0 overflow-hidden rounded-xl">
      <motion.div
        className="w-32 aspect-[2/3] rounded-xl overflow-hidden relative cursor-pointer transition-all duration-300"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
      >
        {/* Background Image */}
        {itinerary.imageUrl ? (
          <Image
            src={itinerary.imageUrl}
            alt={itinerary.title}
            className="absolute inset-0"
            imageClassName="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-600" />
        )}

        {/* Overlay */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-black/10 via-black/5 to-black/10" />

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
          <div className="flex flex-col gap-1">
            <h3 className="text-white font-semibold text-sm leading-tight line-clamp-3 mb-1">
              {itinerary.title}
            </h3>

            {/* Stats */}
            <div className="flex items-center gap-2 text-[10px] text-white/90 font-medium">
              <div className="flex items-center gap-0.5">
                <Calendar className="h-3 w-3" />
                <span>{itinerary.daysCount}d</span>
              </div>
              <div className="flex items-center gap-0.5">
                <MapPin className="h-3 w-3" />
                <span>{itinerary.stopsCount}</span>
              </div>
              {itinerary.likeCount > 0 && (
                <div className="flex items-center gap-0.5">
                  <Heart className="h-3 w-3 fill-current" />
                  <span>{itinerary.likeCount}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ExploreCard component
interface ExploreCardProps {
  onClick?: () => void;
}

const ExploreCard: React.FC<ExploreCardProps> = ({ onClick }) => {
  return (
    <motion.div className="flex-shrink-0 overflow-hidden rounded-xl">
      <motion.div
        className="w-32 aspect-[2/3] rounded-xl overflow-hidden relative cursor-pointer transition-all duration-300"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
      >
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600" />

        {/* Overlay */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-black/10 via-black/5 to-black/10" />

        {/* Center Icon */}
        <div className="absolute inset-0 flex items-center justify-center pb-8">
          <Compass className="h-10 w-10 text-white/90" strokeWidth={1.5} />
        </div>

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-white font-semibold text-sm leading-tight">
              Get inspired
            </h3>
            <p className="text-white/90 text-[10px] font-medium">
              Explore tours
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function LandingPage() {
  const { api } = useRepository();
  const navigate = useNavigate({ from: '/' });
  const search = useSearch({ from: '/' });

  // Parse center and zoom from URL search params
  const urlViewState = useMemo(() => {
    if (search.center && search.zoom) {
      try {
        const center = JSON.parse(search.center) as LngLatLike;
        const zoom = parseFloat(search.zoom);
        if (center && !isNaN(zoom)) {
          return { center, zoom };
        }
      } catch (error) {
        console.warn('Failed to parse view state from URL:', error);
      }
    }
    // Fallback: try to parse old bounds format for backward compatibility
    if (search.bounds) {
      try {
        const parsed = JSON.parse(search.bounds);
        // If it's bounds format, convert to center/zoom
        if (
          Array.isArray(parsed) &&
          parsed.length === 2 &&
          Array.isArray(parsed[0])
        ) {
          const [[swLng, swLat], [neLng, neLat]] = parsed;
          const center: LngLatLike = [(swLng + neLng) / 2, (swLat + neLat) / 2];
          // Estimate zoom from bounds size
          const lngDiff = Math.abs(neLng - swLng);
          const latDiff = Math.abs(neLat - swLat);
          const maxDiff = Math.max(lngDiff, latDiff);
          let zoom = 10;
          if (maxDiff < 0.01) zoom = 16;
          else if (maxDiff < 0.1) zoom = 13;
          else if (maxDiff < 1) zoom = 10;
          else if (maxDiff < 5) zoom = 8;
          else if (maxDiff < 20) zoom = 5;
          else zoom = 2;
          return { center, zoom };
        }
      } catch (error) {
        console.warn('Failed to parse bounds from URL:', error);
      }
    }
    return null;
  }, [search.center, search.zoom, search.bounds]);

  // Calculate initial mode based on URL zoom level (same logic as PublicStopsMap)
  const initialMode = useMemo<ZoomMode>(() => {
    if (urlViewState?.zoom !== undefined) {
      // Same logic as PublicStopsMap: zoom < 2.5 = country, else city
      return urlViewState.zoom < 2.5 ? 'country' : 'city';
    }
    return 'country'; // Default to country if no zoom in URL
  }, [urlViewState?.zoom]);

  const [currentMode, setCurrentMode] = useState<ZoomMode>(initialMode);
  const [zoomToCenter, setZoomToCenter] = useState<
    ((center: LngLatLike, zoom: number) => void) | null
  >(null);
  const [selectedAggregatedStop, setSelectedAggregatedStop] =
    useState<AggregatedStopProjection | null>(null);
  const [isStopPreviewOpen, setIsStopPreviewOpen] = useState(false);

  // State for filtered itineraries from the map
  const [filteredItineraries, setFilteredItineraries] = useState<
    ItineraryProjection[]
  >([]);

  // Current view state (center and zoom) - simpler than bounds
  const [currentViewState, setCurrentViewState] = useState<MapViewState | null>(
    null
  );

  // Current bounds state (still needed for filtering stops)
  const [currentBounds, setCurrentBounds] = useState<LngLatBoundsLike | null>(
    null
  );

  useEffect(() => {
    document.getElementById('citysage-ssr-visible-content')?.remove();
  }, []);

  // Initialize view state from URL on mount
  useEffect(() => {
    if (urlViewState && !currentViewState) {
      setCurrentViewState(urlViewState);
    }
  }, [urlViewState, currentViewState]);

  // Update mode when initialMode changes (e.g., when navigating back with different zoom)
  useEffect(() => {
    setCurrentMode(initialMode);
  }, [initialMode]);

  // Debounced function to update URL with center and zoom
  const debouncedUpdateViewState = useDebouncedCallback(
    (center: LngLatLike, zoom: number) => {
      navigate({
        search: {
          ...search,
          center: JSON.stringify(center),
          zoom: zoom.toString(),
        },
        replace: true,
      });
    },
    1000
  );

  // Helper function to navigate to reel
  const navigateToReel = useCallback(
    (itineraryId: string) => {
      navigate({
        to: '/itineraries/reels/$startId',
        params: { startId: itineraryId },
        search: {
          activeIndex: 0,
          center: currentViewState
            ? JSON.stringify(currentViewState.center)
            : undefined,
          zoom: currentViewState ? currentViewState.zoom.toString() : undefined,
        },
      });
    },
    [navigate, currentViewState]
  );

  // Callback to handle map ready
  const handleMapReady = useCallback(
    (zoomFunction: (center: LngLatLike, zoom: number) => void) => {
      setZoomToCenter(() => zoomFunction);
    },
    []
  );

  // Callback to handle bounds changes from the map (still needed for filtering)
  const handleBoundsChange = useCallback((bounds: LngLatBoundsLike) => {
    setCurrentBounds(bounds);
  }, []);

  // Callback to handle view state changes (center/zoom) - update URL
  const handleViewStateChange = useCallback(
    (center: LngLatLike, zoom: number) => {
      setCurrentViewState({ center, zoom });
      debouncedUpdateViewState(center, zoom);
    },
    [debouncedUpdateViewState]
  );

  // Callback to handle filtered itineraries changes from the map
  const handleFilteredItinerariesChange = useCallback(
    (itineraries: ItineraryProjection[]) => {
      setFilteredItineraries(itineraries);
    },
    []
  );

  // Fetch all itinerary projections - much more efficient than fetching full itineraries
  const { data: allItineraries, isLoading: isLoadingItineraries } = useQuery({
    queryKey: ['itinerary-projections'],
    queryFn: () => api.findAllItineraryProjections(),
  });

  // Fetch all stop projections - much more efficient than fetching all stops
  const { data: allStops } = useQuery({
    queryKey: ['stop-projections'],
    queryFn: () => api.findAllStopProjections(),
  });

  // Determine what to show based on current mode and zoom level
  const displayData = useMemo(() => {
    if (!allStops || allStops.length === 0 || !filteredItineraries)
      return { type: 'countries', items: [] };

    // At country level (high zoom), show countries
    if (currentMode === 'country') {
      const locationMap = new Map<string, LocationData>();

      // Aggregate by country
      allStops.forEach((stopProjection: StopProjection) => {
        if (!stopProjection.placeAddressCountryName) return;

        const country = stopProjection.placeAddressCountryName;

        // Find the specific itinerary for this stop
        const itinerary = filteredItineraries.find(
          (it) => it.id === stopProjection.itineraryId
        );
        if (!itinerary) return;

        if (locationMap.has(country)) {
          const existing = locationMap.get(country)!;
          // Add itinerary if not already present
          if (
            !existing.itineraries.some(
              (i: ItineraryProjection) => i.id === itinerary.id
            )
          ) {
            existing.itineraries.push(itinerary);
            existing.itineraryCount = existing.itineraries.length;
          }
        } else {
          locationMap.set(country, {
            location: country,
            itineraryCount: 1,
            itineraries: [itinerary],
            countryImageUrl: itinerary.countryImageUrl,
          });
        }
      });

      return {
        type: 'countries',
        items: Array.from(locationMap.values()).sort((a, b) =>
          a.location.localeCompare(b.location)
        ),
      };
    } else {
      // At city level (low zoom), show individual itineraries
      return { type: 'itineraries', items: filteredItineraries };
    }
  }, [allStops, currentMode, filteredItineraries]);

  // Handle zoom mode changes
  const handleZoomChange = (mode: ZoomMode) => {
    setCurrentMode(mode);
  };

  // Handle stop marker click
  const handleStopClick = useCallback(
    (aggregatedStop: AggregatedStopProjection) => {
      setSelectedAggregatedStop(aggregatedStop);
      setIsStopPreviewOpen(true);
    },
    []
  );

  // Handle stop preview sheet close
  const handleStopPreviewClose = useCallback(() => {
    setIsStopPreviewOpen(false);
    setSelectedAggregatedStop(null);
  }, []);

  const handleLocationClick = (locationData: LocationData) => {
    // Calculate bounds for all stops in this country
    if (!allStops || allStops.length === 0) return;

    const countryStops = allStops.filter(
      (stopProjection) =>
        stopProjection.placeAddressCountryName === locationData.location
    );

    if (countryStops.length === 0) return;

    // Calculate the bounding box
    const lats = countryStops
      .map((stopProjection) => stopProjection.placeCoordinatesLatitude)
      .filter(Boolean) as number[];
    const lngs = countryStops
      .map((stopProjection) => stopProjection.placeCoordinatesLongitude)
      .filter(Boolean) as number[];

    if (lats.length === 0 || lngs.length === 0) return;

    const bounds = {
      southWest: {
        lat: Math.min(...lats),
        lng: Math.min(...lngs),
      },
      northEast: {
        lat: Math.max(...lats),
        lng: Math.max(...lngs),
      },
    };

    // Add some padding to the bounds
    const latPadding = (bounds.northEast.lat - bounds.southWest.lat) * 0.1;
    const lngPadding = (bounds.northEast.lng - bounds.southWest.lng) * 0.1;

    // Calculate center from bounds
    const center: LngLatLike = [
      (bounds.southWest.lng + bounds.northEast.lng) / 2,
      (bounds.southWest.lat + bounds.northEast.lat) / 2,
    ];

    // Calculate zoom from bounds size
    const lngDiff =
      bounds.northEast.lng - bounds.southWest.lng + lngPadding * 2;
    const latDiff =
      bounds.northEast.lat - bounds.southWest.lat + latPadding * 2;
    const maxDiff = Math.max(lngDiff, latDiff);

    let zoom = 10;
    if (maxDiff < 0.01) zoom = 16;
    else if (maxDiff < 0.1) zoom = 13;
    else if (maxDiff < 1) zoom = 10;
    else if (maxDiff < 5) zoom = 8;
    else if (maxDiff < 20) zoom = 5;
    else zoom = 2;

    // Zoom to center
    if (zoomToCenter) {
      zoomToCenter(center, zoom);
    }
  };
  // Handle explore stories action for welcome modal
  const handleExploreStories = useCallback(() => {
    // Navigate to the first itinerary if available
    if (allItineraries && allItineraries.length > 0) {
      navigateToReel(allItineraries[0].id);
    }
  }, [allItineraries, navigateToReel]);

  return (
    <main className="flex flex-col relative overflow-hidden w-full h-full">
      <header className="sr-only">
        <h1>{HOME_PAGE_SEO.h1}</h1>
      </header>

      {/* Map Section with Location Cards */}
      <section className="w-full relative flex-1 overflow-hidden">
        <PublicStopsMap
          stops={allStops}
          itineraries={allItineraries}
          onZoomChange={handleZoomChange}
          onViewStateChange={handleViewStateChange}
          onMapReady={handleMapReady}
          onStopClick={handleStopClick}
          onFilteredItinerariesChange={handleFilteredItinerariesChange}
          onBoundsChange={handleBoundsChange}
          initialCenter={urlViewState?.center}
          initialZoom={urlViewState?.zoom}
        />

        {/* Cards Section - Overlapping from bottom */}
        <AnimatePresence mode="wait">
          {displayData.items.length > 0 && (
            <motion.section
              key={`${displayData.type}-cards`}
              className="absolute left-0 right-0 z-30 max-w-fit pointer-events-none md:!bottom-0"
              style={{
                // On mobile, account for bottom nav (safe area)
                // On desktop (md+), overridden by md:!bottom-0 class
                bottom: '12px',
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              {/* Cards Container */}
              <div className="relative pb-2 md:pb-4 pr-4 pointer-events-auto">
                <div
                  className="overflow-x-auto [&::-webkit-scrollbar]:hidden"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                >
                  <motion.div
                    className="flex gap-4 px-4 pb-2"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.1,
                          delayChildren: 0.2,
                        },
                      },
                    }}
                  >
                    {/* Always show ExploreCard first */}
                    <motion.div
                      key="explore-card"
                      variants={{
                        hidden: {
                          opacity: 0,
                          x: -20,
                          scale: 0.9,
                        },
                        visible: {
                          opacity: 1,
                          x: 0,
                          scale: 1,
                          transition: {
                            type: 'spring',
                            stiffness: 300,
                            damping: 30,
                          },
                        },
                      }}
                    >
                      <ExploreCard
                        onClick={() => {
                          // Navigate to the first itinerary if available
                          if (allItineraries && allItineraries.length > 0) {
                            navigateToReel(allItineraries[0].id);
                          }
                        }}
                      />
                    </motion.div>

                    {displayData.type === 'countries'
                      ? // Show country location cards
                        (displayData.items as LocationData[]).map(
                          (locationData, index) => (
                            <motion.div
                              key={locationData.location}
                              variants={{
                                hidden: {
                                  opacity: 0,
                                  x: -20,
                                  scale: 0.9,
                                },
                                visible: {
                                  opacity: 1,
                                  x: 0,
                                  scale: 1,
                                  transition: {
                                    type: 'spring',
                                    stiffness: 300,
                                    damping: 30,
                                  },
                                },
                              }}
                            >
                              <LocationCard
                                location={locationData.location}
                                itineraryCount={locationData.itineraryCount}
                                onClick={() =>
                                  handleLocationClick(locationData)
                                }
                                mode={currentMode}
                                countryImageUrl={locationData.countryImageUrl}
                              />
                            </motion.div>
                          )
                        )
                      : // Show individual itinerary cards
                        (displayData.items as ItineraryProjection[]).map(
                          (itinerary, index) => (
                            <motion.div
                              key={itinerary.id}
                              variants={{
                                hidden: {
                                  opacity: 0,
                                  x: -20,
                                  scale: 0.9,
                                },
                                visible: {
                                  opacity: 1,
                                  x: 0,
                                  scale: 1,
                                  transition: {
                                    type: 'spring',
                                    stiffness: 300,
                                    damping: 30,
                                  },
                                },
                              }}
                            >
                              <div
                                onClick={() => {
                                  navigateToReel(itinerary.id);
                                }}
                              >
                                <MiniItineraryCard itinerary={itinerary} />
                              </div>
                            </motion.div>
                          )
                        )}
                  </motion.div>
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </section>

      {/* Aggregated Stop Preview Sheet */}
      <AggregatedStopPreviewSheet
        aggregatedStop={selectedAggregatedStop}
        isOpen={isStopPreviewOpen}
        onClose={handleStopPreviewClose}
      />

      {/* Welcome Modal */}
      <WelcomeModal onExploreStories={handleExploreStories} />
    </main>
  );
}
